// File: noviopus-ats-client/src/Main/sagas.ts
import { takeEvery, spawn, call, fork, take, select, put } from 'redux-saga/effects';
import { REHYDRATE } from 'redux-persist'; 
import { watchToastrQueue } from '@/components/Toastr/sagas';
import messagesSaga from '@/Pages/Messages/sagas'; 
import { inviteConfirmedFlow, signupConfirmedFlow } from '@/features/SignUp/sagas';
import customForm from '@/components/Form/sagas';
import watchCopyToClipBoard from '@/components/CopyToClipBoard/sagas';
import { selectCurrentAccount, selectAuthUser } from '@/features/Auth/selectors';
import watchDBAutocomplete from '@/components/DBAutocomplete/sagas';
import * as AccountSagas from '@/Pages/Account/sagas';
import authRootSaga from '@/features/Auth/sagas'; 
import { SET_CURRENT_USER, UPDATE_CURRENT_USER, SET_CURRENT_ACCOUNT } from '@/features/Auth/authSlice';
import { saga as onBoardingFlowSaga } from '@/features/OnBoardingFlow';
// FIX: Import Job Feature Saga
import { saga as jobFeatureSaga } from '@/features/Job';

import i18n from '@/i18n';
import { setDirection } from '@/features/direction/directionSlice';
import { setLanguage } from '@/features/language/languageSlice';

const REHYDRATE_ACTION = (REHYDRATE as any) || 'persist/REHYDRATE';

function* appStartFlow() {
  yield take(REHYDRATE_ACTION);
}

function* handleAuthUserUpdate() {
    try {
        const user = yield select(selectAuthUser);
        
        if (user && user.preferences && user.preferences.language) {
            const { langName, direction } = user.preferences.language;
            
            if (langName && i18n.language !== langName) {
                yield call([i18n, i18n.changeLanguage], langName);
                yield put(setLanguage(langName));
            }

            if (direction) {
                const lowerDir = direction.toLowerCase();
                yield put(setDirection(lowerDir));
                document.documentElement.dir = lowerDir;
                document.body.dir = lowerDir;
                localStorage.setItem('direction', lowerDir);
            }
        }
    } catch (error) {
        console.error('[MainSaga] Error syncing user preferences:', error);
    }
}

function* watchAuthUserChanges() {
  yield spawn(function* () { yield takeEvery(SET_CURRENT_USER, handleAuthUserUpdate); });
  yield spawn(function* () { yield takeEvery(UPDATE_CURRENT_USER, handleAuthUserUpdate); });
  yield spawn(function* () { yield takeEvery(SET_CURRENT_ACCOUNT, handleAuthUserUpdate); });
}

function* safeSpawn(sagaOrArray: any, name: string) {
  try {
    let target = sagaOrArray;
    if (!target) return;
    if (target.default) target = target.default;
    if (Array.isArray(target)) {
      for (let i = 0; i < target.length; i++) yield* safeSpawn(target[i], `${name}[${i}]`);
      return;
    }
    if (typeof target === 'function') {
      yield spawn(function* () {
        try { yield call(target); } catch (e) { console.error(`[MainSaga] Feature '${name}' crashed:`, e); }
      });
    }
  } catch (err) { console.error(`[MainSaga] Fatal error spawning '${name}':`, err); }
}

export default function* mainSaga() {
  yield* safeSpawn(authRootSaga, 'authRootSaga');
  yield* safeSpawn(onBoardingFlowSaga, 'onBoardingFlowSaga'); 
  
  // FIX: Inject Job Saga Globally so "Create Job" button works from Dashboard/Nav
  yield* safeSpawn(jobFeatureSaga, 'jobFeatureSaga');

  yield* safeSpawn(watchAuthUserChanges, 'watchAuthUserChanges');
  yield* safeSpawn(signupConfirmedFlow, 'signupConfirmedFlow'); 
  yield* safeSpawn(inviteConfirmedFlow, 'inviteConfirmedFlow');
  yield* safeSpawn(appStartFlow, 'appStartFlow');

  yield* safeSpawn(customForm, 'customForm');
  yield* safeSpawn(watchToastrQueue, 'watchToastrQueue');
  yield* safeSpawn(watchDBAutocomplete, 'watchDBAutocomplete');
  yield* safeSpawn(watchCopyToClipBoard, 'watchCopyToClipBoard');

  const currentAccount = yield select(selectCurrentAccount);
  if (currentAccount && currentAccount.onboardingComplete) {
      if (AccountSagas && typeof AccountSagas.activateAccountWatcher === 'function') {
          yield fork(AccountSagas.activateAccountWatcher);
      }
      yield* safeSpawn(messagesSaga, 'messagesSaga');
  }
}